// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

// Internal-only helpers shared by layout.js and toggle.js. Not re-exported from the
// equipmentUtils barrel — this file exists purely to break the circular dependency that
// would otherwise exist between layout.js (relayoutGroup needs toggle.js's
// getAncestorChain) and toggle.js (toggleNode needs these layout helpers).

import {
  PARENT_WIDTH,
  CHILD_WIDTH,
  CHILD_HEIGHT,
  CHILD_SPACING,
  CHILD_SPACING_H,
  PARENT_HEADER_HEIGHT,
  GROUP_NODE_HEIGHT,
  HANDLE_HEIGHT,
  WIDTH_DECREMENT,
} from '../constants/constants';

const getChildren = (nodes, parentId) => nodes.filter(n => n.parentId === parentId && !n.hidden);

// Height needed for a group to fit every boundary handle it renders without them
// overlapping — mirrors GROUP_NODE_HEIGHT's own header + one-handle-row assumption,
// generalized to however many distinct connections this group actually has.
export const resolveClosedGroupHeight = handles => {
  const count = (handles?.inputs?.length ?? 0) + (handles?.outputs?.length ?? 0);
  return Math.max(GROUP_NODE_HEIGHT, PARENT_HEADER_HEIGHT + count * HANDLE_HEIGHT);
};

// Closed subgroups retain their last-open data.width which may be from a different layout mode.
// Always use baseWidth so parent layout calculations are consistent regardless of previous layout.
const effectiveChildWidth = child => {
  if (child.type === 'equipmentGroup' && !child.data?.isOpen) {
    return child.data?.baseWidth ?? PARENT_WIDTH;
  }
  return child.data?.width ?? CHILD_WIDTH;
};

// Pre-computes per-column widths and x-offsets for grid layout.
// Shared by computeGroupDimensions and layoutChildren to avoid duplicating the N×N calculation.
const getGridColumns = children => {
  const N = Math.ceil(Math.sqrt(children.length));
  const colWidths = Array(N).fill(0);
  children.forEach((child, i) => {
    colWidths[i % N] = Math.max(colWidths[i % N], effectiveChildWidth(child));
  });
  const colX = [];
  let x = CHILD_SPACING_H;
  for (let col = 0; col < N; col++) {
    colX.push(x);
    x += colWidths[col] + CHILD_SPACING_H;
  }
  return { N, colWidths, colX };
};

// Returns { height, width } for an open group based on its per-node layout and visible children.
// For horizontal/grid, dimensions grow with content. For vertical, width only expands when a child
export const computeGroupDimensions = (nodes, groupId) => {
  const parent = nodes.find(n => n.id === groupId);
  const layout = parent?.data?.layout || 'vertical';
  const children = getChildren(nodes, groupId);

  const closedHeight = resolveClosedGroupHeight(parent?.data?.handles);

  if (!children.length) {
    return { height: closedHeight, width: parent?.data?.baseWidth ?? PARENT_WIDTH };
  }

  if (layout === 'horizontal') {
    const maxChildHeight = Math.max(...children.map(c => c.data?.height ?? CHILD_HEIGHT));
    const totalChildWidth = children.reduce((sum, c) => sum + effectiveChildWidth(c), 0);
    const base = parent?.data?.baseWidth ?? PARENT_WIDTH;
    return {
      height: Math.max(closedHeight, GROUP_NODE_HEIGHT + CHILD_SPACING + maxChildHeight),
      width: Math.max(base, (children.length + 1) * CHILD_SPACING_H + totalChildWidth),
    };
  }

  if (layout === 'grid') {
    const N = Math.ceil(Math.sqrt(children.length));
    const numRows = Math.ceil(children.length / N);
    const { colWidths } = getGridColumns(children);
    let totalRowHeight = 0;
    for (let row = 0; row < numRows; row++) {
      const rowMax = Math.max(
        ...children.slice(row * N, (row + 1) * N).map(c => c.data?.height ?? CHILD_HEIGHT)
      );
      totalRowHeight += CHILD_SPACING + rowMax;
    }
    const base = parent?.data?.baseWidth ?? PARENT_WIDTH;
    return {
      height: Math.max(closedHeight, GROUP_NODE_HEIGHT + totalRowHeight),
      width: Math.max(base, (N + 1) * CHILD_SPACING_H + colWidths.reduce((s, w) => s + w, 0)),
    };
  }

  // vertical — width only expands when a child overflows baseWidth (e.g. an open horizontal subgroup)
  const base = parent?.data?.baseWidth ?? PARENT_WIDTH;
  const maxChildWidth = Math.max(
    ...children.map(c => {
      const w = effectiveChildWidth(c);
      if (c.type !== 'equipmentGroup') {
        return w;
      }
      const childBase = c.data?.baseWidth ?? PARENT_WIDTH;
      // open subgroup that overflowed its own baseWidth: add margin so the parent visually wraps it
      return w > childBase ? w + WIDTH_DECREMENT : w;
    })
  );
  const totalChildrenHeight = children.reduce(
    (sum, child) =>
      sum +
      (child.data?.height ?? (child.type === 'equipmentGroup' ? GROUP_NODE_HEIGHT : CHILD_HEIGHT)),
    0
  );
  return {
    height: Math.max(
      closedHeight,
      GROUP_NODE_HEIGHT +
        CHILD_SPACING +
        totalChildrenHeight +
        CHILD_SPACING * (children.length - 1)
    ),
    width: Math.max(base, maxChildWidth),
  };
};

/* -------------------------------------------------------
   Child positioning
------------------------------------------------------- */

// Positions visible children of a group according to its per-node layout (horizontal, grid, or vertical).
// Must be called after the parent's data.width is updated so vertical centering is accurate.
export const layoutChildren = (nodes, parentId) => {
  const parent = nodes.find(n => n.id === parentId);
  const layout = parent?.data?.layout || 'vertical';
  const children = getChildren(nodes, parentId);

  let updatedChildMap;

  if (layout === 'horizontal') {
    // Left-to-right on a single row; CHILD_SPACING_H as left margin and gap between items
    let x = CHILD_SPACING_H;
    updatedChildMap = new Map(
      children.map(child => {
        const childWidth = effectiveChildWidth(child);
        const entry = { ...child, position: { x, y: PARENT_HEADER_HEIGHT + CHILD_SPACING } };
        x += childWidth + CHILD_SPACING_H;
        return [child.id, entry];
      })
    );
  } else if (layout === 'grid') {
    // NxN grid (N = ⌈√count⌉); columns share widths, each row starts at the same vertical pixel
    const { N, colX } = getGridColumns(children);
    const numRows = Math.ceil(children.length / N);
    const rowY = [PARENT_HEADER_HEIGHT + CHILD_SPACING];
    for (let row = 0; row < numRows - 1; row++) {
      const rowMax = Math.max(
        ...children.slice(row * N, (row + 1) * N).map(c => c.data?.height ?? CHILD_HEIGHT)
      );
      rowY.push(rowY[row] + rowMax + CHILD_SPACING);
    }
    updatedChildMap = new Map(
      children.map((child, i) => [
        child.id,
        { ...child, position: { x: colX[i % N], y: rowY[Math.floor(i / N)] } },
      ])
    );
  } else {
    // vertical — center each child horizontally; stack top-to-bottom with CHILD_SPACING gaps
    const parentWidth = parent?.data?.width ?? parent?.data?.baseWidth ?? PARENT_WIDTH;
    let y = PARENT_HEADER_HEIGHT + CHILD_SPACING;
    updatedChildMap = new Map(
      children.map(child => {
        const childWidth = effectiveChildWidth(child);
        const entry = {
          ...child,
          position: { x: Math.floor((parentWidth - childWidth) / 2), y },
        };
        y += (child.data?.height ?? CHILD_HEIGHT) + CHILD_SPACING;
        return [child.id, entry];
      })
    );
  }

  return nodes.map(n => updatedChildMap.get(n.id) || n);
};

export const updateSiblings = (nodes, parentId) => {
  const parent = nodes.find(n => n.id === parentId);
  if (!parent?.parentId) {
    return nodes;
  }

  const grandparent = nodes.find(n => n.id === parent.parentId);
  const grandparentLayout = grandparent?.data?.layout || 'vertical';

  // For non-vertical layouts, delegate to layoutChildren which is already layout-aware
  if (grandparentLayout !== 'vertical') {
    return layoutChildren(nodes, parent.parentId);
  }

  const siblings = nodes
    .filter(n => n.parentId === parent.parentId && !n.hidden)
    .sort((a, b) => a.position.y - b.position.y);

  let y = PARENT_HEADER_HEIGHT + CHILD_SPACING;

  const updates = new Map();

  siblings.forEach(s => {
    updates.set(s.id, {
      ...s,
      position: { ...s.position, y },
    });

    y += (s.data?.height ?? CHILD_HEIGHT) + CHILD_SPACING;
  });

  return nodes.map(n => updates.get(n.id) || n);
};
